import { useMemo } from "react";
import {
    Avatar,
    Box,
    Chip,
    Grid,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import MedicalInformationOutlinedIcon from "@mui/icons-material/MedicalInformationOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";

/* ==================================================================
   e-OPD visit record — shared read-only vocabulary
   Visit Summary (ek visit) aur Visit History (kai visits) dono yahi
   blocks use karte hain, taaki dono pages bilkul ek jaise dikhein.
   ================================================================== */

/* ------------------------------------------------------------------ */
/* Design tokens (baaki OPD steps se match karte hain)                 */
/* ------------------------------------------------------------------ */
export const T = {
    ink: "#111C33",
    muted: "#4A5670",
    label: "#2C3A57",
    blue: "#1E88E5",
    blueSoft: "#E8F3FE",
    green: "#0F9D58",
    greenSoft: "#E6F4EA",
    amber: "#9A5B00",
    amberSoft: "#FFF3E0",
    border: "#D8DFEA",
    hairline: "#E3E8F0",
    surface: "#F7F9FC",
};

export const EMPTY = "—";

/** Raw display value — unit alag rakhte hain taaki tiles unhe chhota dikha sakein. */
export const raw = (v) => {
    if (v === null || v === undefined || String(v).trim() === "") return EMPTY;
    return String(v);
};

export const val = (v, suffix = "") => {
    const base = raw(v);
    if (base === EMPTY) return EMPTY;
    return suffix ? `${base} ${suffix}` : base;
};

export const initialsOf = (name) =>
    String(name || "")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase() || "?";

/** Visit ke status ka chip look — completed vs in-progress. */
export const statusChipOf = (visit) =>
    visit?.completed
        ? { label: "Completed", fg: T.green, bg: T.greenSoft }
        : {
            label: `In progress · Step ${visit?.current_step ?? EMPTY} of 7`,
            fg: T.amber,
            bg: T.amberSoft,
        };

/* ------------------------------------------------------------------ */
/* Building blocks                                                     */
/* ------------------------------------------------------------------ */

/** Icon + title wala outlined card — har section ek card me. */
export function SectionCard({ icon, title, count, children, sx }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 2.5,
                borderColor: T.border,
                height: "100%",
                overflow: "hidden",
                backgroundColor: "#fff",
                breakInside: "avoid",
                ...sx,
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                spacing={1.25}
                sx={{
                    px: 2.25,
                    py: 1.5,
                    backgroundColor: T.surface,
                    borderBottom: `1px solid ${T.border}`,
                }}
            >
                <Box sx={{ display: "flex", color: T.blue, "& svg": { fontSize: 18 } }}>{icon}</Box>
                <Typography
                    sx={{
                        flex: 1,
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: ".06em",
                        textTransform: "uppercase",
                        color: T.ink,
                    }}
                >
                    {title}
                </Typography>
                {count > 0 && (
                    <Chip
                        label={count}
                        size="small"
                        sx={{
                            height: 20,
                            minWidth: 24,
                            fontSize: 11,
                            fontWeight: 700,
                            color: T.blue,
                            backgroundColor: T.blueSoft,
                        }}
                    />
                )}
            </Stack>
            <Box sx={{ p: 2.25 }}>{children}</Box>
        </Paper>
    );
}

/** Label left, value right, dashed hairline ke saath. */
export function DetailRow({ label, value }) {
    const empty = value === EMPTY;
    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            spacing={2}
            sx={{
                py: 1.1,
                borderBottom: `1px dashed ${T.hairline}`,
                "&:first-of-type": { pt: 0 },
                "&:last-of-type": { pb: 0, borderBottom: 0 },
            }}
        >
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: T.label, whiteSpace: "nowrap" }}>
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: 13.5,
                    fontWeight: empty ? 400 : 600,
                    color: empty ? "#8A94A6" : T.ink,
                    textAlign: "right",
                    wordBreak: "break-word",
                    textTransform: 'capitalize',
                }}
            >
                {value}
            </Typography>
        </Stack>
    );
}

/** Ek vital ka stat tile. */
export function VitalTile({ label, value, unit }) {
    const empty = value === EMPTY;
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 1.5,
                height: "100%",
                borderRadius: 2,
                borderColor: T.border,
                backgroundColor: empty ? "#FCFDFE" : "#fff",
            }}
        >
            <Typography
                sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: ".05em",
                    textTransform: "uppercase",
                    color: T.muted,
                    mb: 0.75,
                    lineHeight: 1.3,
                }}
            >
                {label}
            </Typography>
            <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography
                    sx={{
                        fontSize: 19,
                        fontWeight: 700,
                        lineHeight: 1.1,
                        color: empty ? "#98A2B6" : T.ink,
                    }}
                >
                    {value}
                </Typography>
                {!empty && unit && (
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.muted }}>{unit}</Typography>
                )}
            </Stack>
        </Paper>
    );
}

export function EmptyNote({ children = "None recorded" }) {
    return (
        <Typography sx={{ fontSize: 13, color: "#7C8698", fontStyle: "italic" }}>
            {children}
        </Typography>
    );
}

export function ChipList({ items, tone = "blue" }) {
    if (!items || items.length === 0) return <EmptyNote />;
    const palette =
        tone === "amber"
            ? { bg: T.amberSoft, fg: T.amber }
            : { bg: T.blueSoft, fg: "#1565C0" };

    return (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" gap={1}>
            {items.map((item, idx) => {
                const label = typeof item === "string" ? item : item?.label;
                return (
                    <Chip
                        key={`${label}-${idx}`}
                        label={label}
                        size="small"
                        icon={item?.badge ? <span /> : undefined}
                        sx={{
                            backgroundColor: palette.bg,
                            color: palette.fg,
                            fontWeight: 600,
                            fontSize: 12,
                            height: 26,
                            borderRadius: "14px",
                            "& .MuiChip-label": { px: 1.25 },
                        }}
                    />
                );
            })}
        </Stack>
    );
}

export function BulletList({ items }) {
    if (!items || items.length === 0) return <EmptyNote />;
    return (
        <Stack spacing={1.25}>
            {items.map((t, i) => (
                <Stack key={i} direction="row" spacing={1.25} alignItems="center">
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            backgroundColor: T.blue,
                            mt: "7px",
                            flexShrink: 0,
                        }}
                    />
                    <Typography sx={{ fontSize: 13.5, color: T.ink, lineHeight: 1.55, textTransform: 'capitalize' }}>{t}</Typography>
                </Stack>
            ))}
        </Stack>
    );
}

/** Shared table shell — zebra rows + uppercase head. */
export function DataTable({ columns, rows, minWidth = 560 }) {
    return (
        <TableContainer
            sx={{
                border: `1px solid ${T.border}`,
                borderRadius: 2,
                "&::-webkit-scrollbar": { height: 6 },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "#D5DCE7", borderRadius: 3 },
            }}
        >
            <Table
                size="small"
                sx={{
                    minWidth,
                    /* Ek jaisi grid lines har jagah — har cell ka bottom + column separator */
                    "& th, & td": {
                        borderBottom: `1px solid ${T.border}`,
                        borderRight: `1px solid ${T.border}`,
                        py: 1.25,
                        px: 2,
                    },
                    "& th:last-of-type, & td:last-of-type": { borderRight: 0 },
                    /* aakhri row ka bottom container ki border se double na ho */
                    "& tbody tr:last-of-type td": { borderBottom: 0 },
                }}
            >
                <TableHead>
                    <TableRow>
                        {columns.map((c) => (
                            <TableCell
                                key={c.key}
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: ".05em",
                                    textTransform: "uppercase",
                                    color: T.label,
                                    backgroundColor: T.surface,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {c.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, idx) => (
                        <TableRow key={idx} sx={{ "&:nth-of-type(even)": { backgroundColor: "#FBFCFE" } }}>
                            {columns.map((c, ci) => (
                                <TableCell
                                    key={c.key}
                                    sx={{
                                        fontSize: 13,
                                        color: ci === 0 ? T.ink : "#2E3A52",
                                        fontWeight: ci === 0 ? 600 : 500,
                                    }}
                                >
                                    {row[c.key]}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

/* ------------------------------------------------------------------ */
/* Patient banner — sheet ke top par gradient strip                    */
/* ------------------------------------------------------------------ */
/** Left me avatar + naam + quick chips; `children` right side me aata hai. */
export function PatientBanner({ patient = {}, children }) {
    return (
        <Box
            sx={{
                px: { xs: 2.5, md: 3.5 },
                py: { xs: 2.5, md: 3 },
                background: "linear-gradient(135deg, #F4F9FF 0%, #EDF3FF 100%)",
                borderBottom: `1px solid ${T.border}`,
            }}
        >
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                gap={2}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                        sx={{
                            width: 52,
                            height: 52,
                            bgcolor: T.blue,
                            color: "#fff",
                            fontSize: 18,
                            fontWeight: 700,
                        }}
                    >
                        {initialsOf(patient.name)}
                    </Avatar>
                    <Box>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, color: T.ink, lineHeight: 1.25 }}>
                            {raw(patient.name)}
                        </Typography>
                        <Stack
                            direction="row"
                            spacing={1}
                            useFlexGap
                            flexWrap="wrap"
                            gap={0.75}
                            sx={{ mt: 0.75 }}
                        >
                            {[
                                patient.age || patient.gender_name
                                    ? `${patient.age ?? EMPTY} yrs · ${patient.gender_name ?? EMPTY}`
                                    : null,
                                patient.mobile_no || null,
                                patient.abha_number ? `ABHA ${patient.abha_number}` : null,
                            ]
                                .filter(Boolean)
                                .map((text) => (
                                    <Chip
                                        key={text}
                                        label={text}
                                        size="small"
                                        sx={{
                                            height: 24,
                                            fontSize: 11.5,
                                            fontWeight: 600,
                                            color: "#1565C0",
                                            backgroundColor: "rgba(255,255,255,.85)",
                                            border: "1px solid rgba(30,136,229,.18)",
                                        }}
                                    />
                                ))}
                        </Stack>
                    </Box>
                </Stack>

                {children}
            </Stack>
        </Box>
    );
}

/* ------------------------------------------------------------------ */
/* Ek visit ka poora section grid                                      */
/* ------------------------------------------------------------------ */
/**
 * Ek visit ke saare read-only sections.
 * `showPatient` false karne par Patient card chhod deta hai — Visit
 * History me patient details accordions ke bahar ek hi baar dikhti hain.
 */
export function VisitDetailSections({ visit, showPatient = true }) {
    const {
        general = {},
        patient = {},
        chief_complaints = [],
        history = [],
        vitals = {},
        screening_positive = [],
        lab_tests = [],
        provisional_diagnosis = "",
        counselling = [],
        packages = [],
        drugs = [],
    } = visit || {};

    const complaintsList = useMemo(
        () => (chief_complaints || []).map((cc) => cc.symptom_name).filter(Boolean),
        [chief_complaints]
    );
    const counsellingList = useMemo(
        () => (counselling || []).map((c) => c.con_name).filter(Boolean),
        [counselling]
    );
    const packagesList = useMemo(
        () => (packages || []).map((p) => p.display_name).filter(Boolean),
        [packages]
    );
    const screeningList = useMemo(
        () =>
            (screening_positive || [])
                .map((s) => (typeof s === "string" ? s : s?.name || s?.display_name))
                .filter(Boolean),
        [screening_positive]
    );

    const vitalTiles = [
        { label: "Weight", value: raw(vitals.weight), unit: "kg" },
        { label: "Height", value: raw(vitals.height), unit: "cm" },
        { label: "BMI", value: raw(vitals.bmi), unit: "kg/m²" },
        { label: "Temperature", value: raw(vitals.temp), unit: "°F" },
        { label: "Pulse Rate", value: raw(vitals.pulse_rate), unit: "bpm" },
        { label: "Blood Pressure", value: raw(vitals.bp), unit: "mmHg" },
        { label: "Respiratory Rate", value: raw(vitals.resp_rate), unit: "/min" },
        { label: "Random Blood Sugar", value: raw(vitals.rand_blood_sugar), unit: "mg/dL" },
        { label: "Haemoglobin", value: raw(vitals.haemoglobin), unit: "g/dL" },
        { label: "Abdominal Circ.", value: raw(vitals.abd_circum), unit: "cm" },
        { label: "Hip Circ.", value: raw(vitals.hip_circum), unit: "cm" },
        { label: "Waist / Hip Ratio", value: raw(vitals.waist_hip_ratio), unit: "" },
    ];

    return (
        <Grid container spacing={2.5}>
            {/* General + Patient */}
            <Grid size={{ xs: 12, md: showPatient ? 6 : 12 }}>
                <SectionCard icon={<EventNoteOutlinedIcon />} title="General Info">
                    <DetailRow label="Date / Time" value={val(general.visit_datetime)} />
                    <DetailRow label="AAM Unit" value={val(general.facility_name)} />
                    <DetailRow label="CHO" value={val(general.cho_name)} />
                    <DetailRow label="CHO Phone" value={val(general.cho_mobile)} />
                    <DetailRow label="ASHA Name" value={val(general.asha_name)} />
                    <DetailRow label="ASHA Phone" value={val(general.asha_mobile)} />
                </SectionCard>
            </Grid>

            {showPatient && (
                <Grid size={{ xs: 12, md: 6 }}>
                    <SectionCard icon={<PersonOutlineOutlinedIcon />} title="Patient">
                        <DetailRow label="Name" value={val(patient.name)} />
                        <DetailRow
                            label="Age / Sex"
                            value={
                                patient.age || patient.gender_name
                                    ? `${patient.age ?? EMPTY} / ${patient.gender_name ?? EMPTY}`
                                    : EMPTY
                            }
                        />
                        <DetailRow label="Phone" value={val(patient.mobile_no)} />
                        <DetailRow label="ABHA Number" value={val(patient.abha_number)} />
                        <DetailRow label="ABHA Address" value={val(patient.abha_address)} />
                        <DetailRow label="PMJAY ID" value={val(patient.pmjay_id)} />
                        <DetailRow label="Address" value={val(patient.address)} />
                    </SectionCard>
                </Grid>
            )}

            {/* Complaints + History */}
            <Grid size={{ xs: 12, md: 6 }}>
                <SectionCard
                    icon={<ReportProblemOutlinedIcon />}
                    title="Chief Complaints"
                    count={complaintsList.length}
                >
                    <ChipList items={complaintsList} />
                </SectionCard>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <SectionCard
                    icon={<HistoryOutlinedIcon />}
                    title="Brief History"
                    count={(history || []).length}
                >
                    <BulletList items={history} />
                </SectionCard>
            </Grid>

            {/* Vitals */}
            <Grid size={12}>
                <SectionCard icon={<MonitorHeartOutlinedIcon />} title="Vitals & Clinical Examination">
                    <Grid container spacing={1.5}>
                        {vitalTiles.map((v) => (
                            <Grid key={v.label} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                                <VitalTile {...v} />
                            </Grid>
                        ))}
                    </Grid>

                    {vitals.findings && (
                        <Box sx={{ mt: 2.5 }}>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: ".05em",
                                    textTransform: "uppercase",
                                    color: T.muted,
                                    mb: 1,
                                }}
                            >
                                Clinical Examination Findings
                            </Typography>
                            <Box
                                sx={{
                                    p: 1.75,
                                    borderRadius: 2,
                                    backgroundColor: T.surface,
                                    borderLeft: `3px solid ${T.blue}`,
                                    border: `1px solid ${T.border}`,
                                    borderLeftWidth: 3,
                                    borderLeftColor: T.blue,
                                }}
                            >
                                <Typography sx={{ fontSize: 13.5, color: T.ink, lineHeight: 1.6, textTransform: 'capitalize' }}>
                                    {vitals.findings}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </SectionCard>
            </Grid>

            {/* Screening + Diagnosis */}
            <Grid size={{ xs: 12, md: 6 }}>
                <SectionCard
                    icon={<FactCheckOutlinedIcon />}
                    title="Screening Positive"
                    count={screeningList.length}
                >
                    <ChipList items={screeningList} tone="amber" />
                </SectionCard>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <SectionCard icon={<MedicalInformationOutlinedIcon />} title="Provisional Diagnosis">
                    {provisional_diagnosis ? (
                        <Typography sx={{ fontSize: 13.5, color: T.ink, lineHeight: 1.6, textTransform: 'capitalize' }}>
                            {provisional_diagnosis}
                        </Typography>
                    ) : (
                        <EmptyNote />
                    )}
                </SectionCard>
            </Grid>

            {/* Counselling + Packages */}
            <Grid size={{ xs: 12, md: 6 }}>
                <SectionCard
                    icon={<RecordVoiceOverOutlinedIcon />}
                    title="Counselling"
                    count={counsellingList.length}
                >
                    <ChipList items={counsellingList} />
                </SectionCard>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <SectionCard
                    icon={<Inventory2OutlinedIcon />}
                    title="Essential Package of Services and Counselling Provided"
                    count={packagesList.length}
                >
                    <ChipList items={packagesList} />
                </SectionCard>
            </Grid>

            {/* Diagnostic tests */}
            <Grid size={12}>
                <SectionCard
                    icon={<ScienceOutlinedIcon />}
                    title="Lab Tests"
                    count={(lab_tests || []).length}
                >
                    {(lab_tests || []).length === 0 ? (
                        <EmptyNote>No lab tests prescribed</EmptyNote>
                    ) : (
                        <DataTable
                            minWidth={480}
                            columns={[
                                { key: "test", label: "Test" },
                                { key: "result", label: "Result" },
                                { key: "status", label: "Status" },
                            ]}
                            rows={lab_tests.map((t) => ({
                                test: raw(t.test_name),
                                result: val(t.result_val),
                                status: val(t.status_label),
                            }))}
                        />
                    )}
                </SectionCard>
            </Grid>

            {/* Drugs */}
            <Grid size={12}>
                <SectionCard
                    icon={<MedicationOutlinedIcon />}
                    title="e-Consultation & Drugs Dispensed"
                    count={(drugs || []).length}
                >
                    {(drugs || []).length === 0 ? (
                        <EmptyNote>No drugs prescribed or dispensed</EmptyNote>
                    ) : (
                        <DataTable
                            minWidth={640}
                            columns={[
                                { key: "drug", label: "Drug" },
                                { key: "dosage", label: "Dosage" },
                                { key: "frequency", label: "Frequency" },
                                { key: "duration", label: "Duration" },
                                { key: "instruction", label: "Instruction" },
                            ]}
                            rows={drugs.map((d) => ({
                                drug: raw(d.medicine_name),
                                dosage: val(d.dosage),
                                frequency: val(d.frequency),
                                duration: val(d.duration),
                                instruction: val(d.spl_instruct),
                            }))}
                        />
                    )}
                </SectionCard>
            </Grid>
        </Grid>
    );
}
